/**
 * 69.31 (DECISIONS 269 (1), the owner: "Let's start with dump arm for rearm"): the dump's arm — sheet A of
 * design_sheets/rearm_robot.html, a knuckle boom on a skid with four outrigger pads, its jaws round a crate; the stacker crane's
 * look a size down, in the motor pool's kit (orbitShips' `Spec`: no cab, a sensor puck, an amber beacon, caution bands, AUTO,
 * the livery). Built as moving parts for play — the skid, the slewing column, the main boom, the outer boom, the head with its
 * jaws (a draw each) and the crate in the jaws (ammoBoxes' crate of the round's type) — posed every frame (`poseRearmArm`) through
 * a crate's cycle (`rearmArmCycle`: pick at the stack, swing, set at the vehicle's loading point; a crate a type's `per` rounds).
 * It draws today's rearm (core/munitions.ts): the pace a round is the sim's, the arm's swing fitted to it. Presentation only; the Lab's *Dump arm*
 * row (?rearmarm=1), with Ammunition at the dumps.
 *
 * Metres, a person 1.8 (the dump's group scales it, as the dump's crates).
 */
import * as THREE from 'three';
import { Spec, mergedTier, type Livery } from './orbitShips';
import type { UnitPalette } from './unitPalette';
import type { AmmoType } from '../types';
import { buildAmmoCrate } from './ammoBoxes';

let shownOverride: boolean | null = null;
/** The Lab's *Dump arm* row (null: the page's ?rearmarm=1). */
export function setRearmArmShown(on: boolean | null): void { shownOverride = on; }
/** 69.31: whether each dump's arm is drawn in play (and the tank built with its ammunition door) — off by default. */
export function rearmArmShown(): boolean {
  if (shownOverride !== null) return shownOverride;
  try { return typeof location !== 'undefined' && new URLSearchParams(location.search).get('rearmarm') === '1'; } catch { return false; }
}

/**
 * The arm's sizes (metres): the skid's top, the main boom's pivot over it, the booms, the head over the crate's bottom (its
 * jaws hang below it), and where the jaws rest folded over the skid.
 */
export const REARM_ARM = { skid: 0.35, pivot: 1.05, a: 3.2, b: 3.0, head: 0.8, rest: new THREE.Vector3(0, 0.55, 1.5) };

export interface RearmArm {
  root: THREE.Group;
  /** turns about y (the slew) */
  slew: THREE.Group;
  /** pitch about x: the main boom at the pivot, the outer at the knuckle; the head is kept upright */
  boomA: THREE.Group;
  boomB: THREE.Group;
  head: THREE.Group;
  /** the crate in the jaws, made per round type the first time it carries one */
  crates: Partial<Record<AmmoType, THREE.Mesh>>;
  p: UnitPalette;
  triangles: number;
}

let armMat: THREE.MeshLambertMaterial | null = null;
const ARM_MAT = () => (armMat ??= new THREE.MeshLambertMaterial({ vertexColors: true }));
function part(livery: Livery, p: UnitPalette, build: (s: Spec) => void): THREE.Mesh {
  const s = new Spec(livery);
  build(s);
  const g = mergedTier(s, p, false);
  g.setAttribute('color', g.getAttribute('paint'));
  g.deleteAttribute('glow');
  const m = new THREE.Mesh(g, ARM_MAT());
  m.name = 'rearm arm part';
  return m;
}
const V = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z);

/** The arm at rest (its jaws folded over the skid's front, facing +z), in the team's livery. */
export function buildRearmArm(p: UnitPalette, livery: Livery): RearmArm {
  const { skid: top, pivot, a, b } = REARM_ARM;
  const root = new THREE.Group(); root.name = 'rearm arm';
  // the skid: its deck and edge, four outrigger legs and pads, AUTO on its side, the beacon; the slewing ring
  const base = part(livery, p, s => {
    s.box(1.8, 0.3, 1.8, 0, 0.15, 0, 'team'); s.box(1.82, 0.06, 1.82, 0, 0.32, 0, 'teamLt');
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) { s.box(0.18, 0.32, 0.18, sx * 1.05, 0.16, sz * 1.05, 'caution'); s.box(0.42, 0.06, 0.42, sx * 1.05, 0.03, sz * 1.05, 'machDk'); }
    s.text('AUTO', 0.91, 0.16, 0, 0.16, 0.9, 'caution');
    s.sphere(0.1, -0.7, top + 0.1, -0.7, 'caution');
    s.cyl(0.5, 0.35, 'y', 0, top + 0.17, 0, 'machDk', { seg: 14 });
  });
  root.add(base);
  const slew = new THREE.Group(); slew.position.y = top; root.add(slew);
  slew.add(part(livery, p, s => {
    s.cyl(0.3, 0.7, 'y', 0, 0.7, 0, 'team', { seg: 10 });
    s.cyl(0.14, 0.17, 'y', 0, pivot + 0.2 + 0.09, -0.25, 'machDk', { seg: 10 }); s.cyl(0.116, 0.07, 'y', 0, pivot + 0.2 + 0.21, -0.25, 'cream', { seg: 10 });   // the sensor puck
  }));
  const boomA = new THREE.Group(); boomA.position.y = pivot; slew.add(boomA);
  boomA.add(part(livery, p, s => {
    s.rod(V(0, 0, 0), V(0, 0, a), 0.17, 'team', { seg: 6 });
    s.rod(V(0, -0.24, 0.1), V(0, -0.1, a * 0.5), 0.07, 'machLt', { seg: 6 });   // its ram, under it
    s.box(0.38, 0.38, 0.38, 0, 0, a, 'machDk');                                // the knuckle
  }));
  const boomB = new THREE.Group(); boomB.position.z = a; boomA.add(boomB);
  boomB.add(part(livery, p, s => s.rod(V(0, 0, 0), V(0, 0, b), 0.13, 'machLt', { seg: 6 })));
  const head = new THREE.Group(); head.position.z = b; boomB.add(head);
  head.add(part(livery, p, s => {
    s.cyl(0.16, 0.25, 'y', 0, -0.12, 0, 'machDk', { seg: 10 });   // the rotator
    s.box(1.2, 0.1, 0.16, 0, -0.25, 0, 'caution');                 // the cross-head
    for (const sx of [-1, 1]) s.box(0.06, 0.5, 0.14, sx * 0.58, -0.53, 0, 'machDk');   // the fingers either side of the crate
  }));
  let triangles = 0;
  root.traverse(o => { const m = o as THREE.Mesh; if (m.isMesh) triangles += m.geometry.attributes.position.count / 3; });
  const arm: RearmArm = { root, slew, boomA, boomB, head, crates: {}, p, triangles: Math.round(triangles) };
  poseRearmArm(arm, REARM_ARM.rest, null);
  return arm;
}

/**
 * The arm reaching its jaws to `E` (the bottom of the crate they hold, in the arm's frame), carrying a crate of `carry` (null:
 * empty): slewed toward it, the knee up, the head upright; past its reach, as far toward it as the booms go. Returns where the
 * crate's bottom is (E, or the reach's end).
 */
export function poseRearmArm(arm: RearmArm, E: THREE.Vector3, carry: AmmoType | null): THREE.Vector3 {
  const { skid, pivot, a, b, head } = REARM_ARM;
  const hx = E.x, hy = E.y + head, hz = E.z, py = skid + pivot;
  const yaw = Math.atan2(hx, hz), h = Math.hypot(hx, hz), dy = hy - py;
  const d = Math.max(Math.abs(a - b) + 0.05, Math.min(a + b - 0.05, Math.hypot(h, dy))), phi = Math.atan2(dy, h);
  const al = Math.acos(Math.max(-1, Math.min(1, (a * a + d * d - b * b) / (2 * a * d))));
  const eA = phi + al, kh = Math.cos(eA) * a, ky = Math.sin(eA) * a, Hh = Math.cos(phi) * d, Hy = Math.sin(phi) * d;
  const eB = Math.atan2(Hy - ky, Hh - kh);
  arm.slew.rotation.y = yaw;
  arm.boomA.rotation.x = -eA;          // (+z up by eA: a turn of −eA about x)
  arm.boomB.rotation.x = -(eB - eA);
  arm.head.rotation.x = eB;            // (upright again)
  for (const [t, m] of Object.entries(arm.crates) as Array<[AmmoType, THREE.Mesh]>) m.visible = t === carry;
  if (carry && !arm.crates[carry]) {
    const c = buildAmmoCrate(carry, arm.p); c.position.y = -head; arm.head.add(c); arm.crates[carry] = c;
  }
  return new THREE.Vector3(Math.sin(yaw) * Hh, py + Hy - head, Math.cos(yaw) * Hh);
}

/** Smoothstep of `t` across [a, b]. */
const ease = (t: number, a: number, b: number) => { const k = Math.max(0, Math.min(1, (t - a) / (b - a))); return k * k * (3 - 2 * k); };
/** Between `p` and `q` by `k` round the arm's pedestal (its bearing, its reach out and its height), lifted `arc` at the middle. */
export function swingBetween(p: THREE.Vector3, q: THREE.Vector3, k: number, arc = 0, out = new THREE.Vector3()): THREE.Vector3 {
  const y0 = Math.atan2(p.x, p.z), r0 = Math.hypot(p.x, p.z), y1 = Math.atan2(q.x, q.z), r1 = Math.hypot(q.x, q.z);
  let dy = y1 - y0;
  if (dy > Math.PI) dy -= 2 * Math.PI; else if (dy < -Math.PI) dy += 2 * Math.PI;
  const yaw = y0 + dy * k, r = r0 + (r1 - r0) * k;
  return out.set(Math.sin(yaw) * r, p.y + (q.y - p.y) * k + arc * Math.sin(Math.PI * k), Math.cos(yaw) * r);
}

/**
 * The cycle's stages (shares of a crate's worth of rounds): from the stack with the crate gripped (the stack shows one fewer the
 * moment it is taken), up, swing, down to the loading point, let go, up, swing back, down onto the next crate open.
 */
export const REARM_CYCLE = { lift: 0.08, swung: 0.4, set: 0.5, release: 0.53, clear: 0.6, back: 0.92 };

/**
 * Where the jaws are `k` (0–1) through a crate's cycle — the crate taken off the stack at `pick` (its top) at 0, swung, set at
 * `set` (where it goes in), the jaws back down on the next one at 1 — and whether they carry it. It ends where it starts, so
 * one crate runs into the next.
 */
export function rearmArmCycle(k: number, pick: THREE.Vector3, set: THREE.Vector3, out = new THREE.Vector3()): { at: THREE.Vector3; carry: boolean } {
  const C = REARM_CYCLE, lift = REARM_ARM.head * 1.1;
  const overPick = _a.copy(pick).setY(pick.y + lift), overSet = _b.copy(set).setY(set.y + lift);
  if (k < C.lift) out.copy(pick).lerp(overPick, ease(k, 0, C.lift));
  else if (k < C.swung) swingBetween(overPick, overSet, ease(k, C.lift, C.swung), 0.4, out);
  else if (k < C.clear) out.copy(overSet).lerp(set, ease(k, C.swung, C.set) - ease(k, C.release, C.clear));
  else if (k < C.back) swingBetween(overSet, overPick, ease(k, C.clear, C.back), 0.4, out);
  else out.copy(overPick).lerp(pick, ease(k, C.back, 1));
  return { at: out, carry: k < C.release };
}
const _a = new THREE.Vector3(), _b = new THREE.Vector3();
